import React, { PureComponent } from "react";
import { PropTypes } from "prop-types";
import { UIRouter, UIView, pushStateLocationPlugin } from "@uirouter/react";
import { connect } from "react-redux";
import axios from "axios";
import i18n from "i18next";

import { defaultLanguage } from "./../../helpers/Language/languageHelpers";
import { setRouterState } from "../../actions";
import App from "../App";
import Auth from "../../helpers/Authentication/Auth";
import advancedSearchStates from "./advancedSearchStates";
import authenticationStates from "./authenticationStates";
import billsOfLadingStates from "./billsOfLadingStates";
import contactUsStates from "./contactUsStates";
import customerStates from "./customerStates";
import errorStates from "./errorStates";
import helpStates from "./helpStates";
import millOrderStates from "./millOrderStates";
import notificationStates from "./notificationStates";
import ordersTrackingStates from "./ordersTrackingStates";
import requireAtLeastOneRoleHook from "../../helpers/Authorization/requireAtLeastOneRoleHook";
import requireRolesHook from "../../helpers/Authorization/requireRolesHook";
import requiresAuthHook from "../../helpers/Authentication/requiresAuthHook";

const rootState = {
  name: "root",
  abstract: true,
  url: "",
  component: App
};

const orderedStates = [
  rootState,
  ...millOrderStates,
  ...billsOfLadingStates,
  ...ordersTrackingStates,
  ...customerStates,
  ...authenticationStates,
  ...errorStates,
  ...helpStates,
  ...advancedSearchStates,
  ...notificationStates,
  ...contactUsStates
];

const plugins = [pushStateLocationPlugin];

class Router extends PureComponent {
  static propTypes = {
    setRouterState: PropTypes.func.isRequired,
    routerParams: PropTypes.object,
    routerState: PropTypes.object,
    userMetadata: PropTypes.object
  };

  static defaultProps = {
    routerState: {},
    userMetadata: {}
  };

  constructor(props) {
    super(props);
    const savedLanguage = this.handleSavedLocalStorage("language", defaultLanguage);

    i18n.changeLanguage(savedLanguage);
    axios.defaults.headers.common["Accept-Language"] = savedLanguage;

    axios.interceptors.request.use(config => {
      const isAuthenticated = Auth.isAuthenticated();
      if (!isAuthenticated) {
        Auth.pushLastRequestedState(this.props.routerState, this.props.routerParams);
        new Auth().login();
      }

      return config;
    });

    // Sera activé au besoin seulement dans les environnements de tests afin de cacher le nom des clients non-public lors de démos.
    if (/true/i.test(process.env.REACT_APP_HIDE_CUSTOMER_NAME)) {
      const customNames = {
        "ASTRO CARTON HONDURAS": "CONTAINERBOARD CUSTOMER",
        FORMICA: "PAPERBOARD CUSTOMER",
        "INSERTS EAST": "NEWSPRINT CUSTOMER"
      };

      const hideCustomerNames = (key, value) => {
        if (value) {
          switch (key) {
            case "customerName":
              return customNames[value] || "CUSTOMER NAME";
            case "endUserId":
              return customNames[value] || "CUSTOMER NAME";
            case "line1":
              return "ADDRESS";
            case "line2":
              return "LINE 2";
            case "line3":
              return "LINE 3";
            case "line4":
              return "LINE 4";
            case "city":
              return "CITY";
            case "province":
              return "PROVINCE";
            case "country":
              return "COUNTRY";
            case "postalCode":
              return "POSTALCODE";
            case "carrierName":
              return "CARRIER NAME";
            case "careOf":
              return "C/O";
            case "locationName":
              return "LOCATION NAME";
            case "vehicleId":
              return "VEHICLE ID";
            case "bookingNumber":
              return "BOOKING NUMBER";
            default:
              return value;
          }
        }

        return value;
      };

      axios.interceptors.response.use(response => {
        if (response.status === 200) {
          response.data = JSON.parse(JSON.stringify(response.data), hideCustomerNames);
        }
        return response;
      });
    }
  }

  componentDidUpdate() {
    const metadataLanguage = this.props.userMetadata.language;
    const savedLanguage = localStorage.getItem("language");

    if (metadataLanguage && savedLanguage !== metadataLanguage) {
      localStorage.setItem("language", metadataLanguage);
      location.reload();
    }
  }

  handleSavedLocalStorage = (item, defaultData) => {
    let data = localStorage.getItem(item);
    if (!data) {
      localStorage.setItem(item, defaultData);
      data = defaultData;
    }

    return data;
  };

  configRouter = router => {
    router.urlRouter.otherwise("/404");
    router.urlRouter.sort((a, b) => a.$id - b.$id);

    router.transitionService.onFinish({}, transition => this.props.setRouterState(transition));
    router.transitionService.onBefore({ to: "logout" }, () => {
      new Auth().logout();
      return false;
    });

    router.transitionService.onBefore({ to: "callback" }, transition => {
      return Auth.isAuthenticated() ? transition.router.stateService.target("home") : null;
    });

    router.transitionService.onBefore(requireAtLeastOneRoleHook.criteria, requireAtLeastOneRoleHook.callback);
    router.transitionService.onBefore(requiresAuthHook.criteria, requiresAuthHook.callback);
    router.transitionService.onBefore(requireRolesHook.criteria, requireRolesHook.callback);
  };

  render() {
    return (
      <UIRouter plugins={plugins} states={orderedStates} config={this.configRouter}>
        <UIView />
      </UIRouter>
    );
  }
}

const mapStateToProps = state => {
  return {
    routerState: state.app.routerState,
    routerParams: state.app.routerParams,
    userMetadata: state.app.userMetadata
  };
};

export default connect(
  mapStateToProps,
  { setRouterState }
)(Router);



// WEBPACK FOOTER //
// ./src/components/Router/index.js